import {
    AfterViewInit,
    Component,
    inject,
    Input,
    OnChanges,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InterfaceAppServiceRegister } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { DynamicFieldDirective } from '@app/modules/dynamic-fields/dynamic-field/dynamic-field.directive';
import { FactorRowService } from '@app/modules/factor-row/services/factor-row.service';
import { FuiDestroyService } from '@services/destroy.service';
import { NotifierService } from '@services/notifier.service';
import { takeUntil } from 'rxjs';
import { fieldsTypes } from '../form-filter.constants';
import { FormFilterFieldsService } from './form-filter-fields.service';

@Component({
    selector: 'app-form-filter-fields',
    templateUrl: './form-filter-fields.component.html',
    providers: [FuiDestroyService],
})
export class FormFilterFieldsComponent implements OnInit, OnChanges, AfterViewInit {
    readonly #destroy$ = inject(FuiDestroyService);
    private formBuilder = inject(FormBuilder);
    private notifier = inject(NotifierService);

    appService = inject(AppService);
    factorRowService = inject(FactorRowService);
    formFilterFieldsService = inject(FormFilterFieldsService);

    fieldsTypes = fieldsTypes;

    /** Форма */
    #formDynamic: FormGroup;

    /** Наличие адреса в форме */
    isFormAddress = false;

    /** Флаг изменения адреса */
    isSetAddressChange = false;

    /** Данные для селекта Регион уже запрошены */
    isRegionRequested = false;

    /** Данные для селекта Регион уже загрузились */
    isRegionLoaded = false;

    /** Загрузка адресов */
    isLoadAddress: {
        region: { [key: string]: boolean };
        district: { [key: string]: boolean };
        city: { [key: string]: boolean };
    } = {
        region: {},
        district: {},
        city: {},
    };

    /** Данные адреса */
    addressData: {
        region?: { id: string; name: string }[];
        district?: { [key: string]: { id: string; name: string }[] };
        city?: { [key: string]: { id: string; name: string }[] };
    } = { region: [], district: {}, city: {} };

    /** Имена адресов */
    addressDataName: { [key: string]: string } = {};

    @ViewChildren(DynamicFieldDirective) dynamicFields!: QueryList<DynamicFieldDirective>;

    @Input()
    set formDynamic(form: FormGroup) {
        this.#formDynamic = form;
        this.#onFormChanged();
    }

    get formDynamic(): FormGroup {
        return this.#formDynamic;
    }

    /** Данные фильтра */
    @Input() settingData: any;

    /**  Списки */
    @Input() registers: { [key: string]: InterfaceAppServiceRegister[] } = {};

    ngOnInit() {
        this.createForm(this.settingData);
    }

    ngOnChanges(changes: SimpleChanges) {
        // Форма и ее настройки обновились (например, при смене сохраненных поисков)
        if (changes.formDynamic && changes.settingData) {
            this.#onFormChanged();
        }
    }

    /* Запрашиваем фиас один раз при открытии фильтра */
    ngAfterViewInit() {
        if (!this.isRegionRequested && this.isFormAddress) {
            this.isRegionRequested = true;
            this.addressData.region = [];
            this.appService
                .getFiasAddressByApi({
                    type: '',
                    method: 'get_regions',
                    data: {},
                })
                .pipe(takeUntil(this.#destroy$))
                .subscribe({
                    next: (result) => {
                        const resultData = result.data ?? [];
                        resultData.forEach((region) => {
                            this.addressData.region.push({ id: region.id, name: region.fullname });
                            this.addressDataName[region.id] = region.fullname;
                        });
                        if (this.dynamicFields) {
                            this.dynamicFields.forEach((fieldItem: any) => {
                                if (fieldItem.id && fieldItem.id.match('(_region)$')) {
                                    fieldItem.setOptions(this.addressData.region);
                                }
                            });
                        }
                        this.isRegionLoaded = true;
                        this.isRegionRequested = false;
                    },
                    error: () => {
                        this.addressData.region = [];
                        this.notifier.warning(this.appService.language_dictionary?.fias_service_is_unavailable);
                        this.isRegionLoaded = false;
                        this.isRegionRequested = false;
                    },
                });
        }
    }

    /**
     * Если форма обновилась, нужно построить новую
     * @private
     */
    #onFormChanged() {
        if (this.formDynamic && this.settingData) {
            // Нужен чтобы точно обновились данные
            setTimeout(() => {
                this.createForm(this.settingData);
            }, 0);
        }
    }

    public createForm(data): void {
        this.formFilterFieldsService.entityGrid = [];
        if (data && data.settings && data.settings.length) {
            this.formFilterFieldsService.entityGrid = data.settings.sort((a, b) => a.y - b.y);
            for (const entity of this.formFilterFieldsService.entityGrid) {
                if (!entity.fields || !entity.fields.length) {
                    continue;
                }
                this.formFilterFieldsService.fieldGrid[entity.name] = entity.fields.sort((a, b) => a.y - b.y);
                this.formFilterFieldsService.fieldGrid[entity.name].forEach((field) => this.addFormField(field));
            }
        }
        this.formFilterFieldsService.isInitForm$.next(true);
    }

    /**
     * Добавление на форму поля
     * @param field поле
     */
    addFormField(field) {
        let value =
            this.settingData.query && this.settingData.query[field.name] ? this.settingData.query[field.name] : null;
        // Если пришло true / false
        if (this.settingData.query && typeof this.settingData.query[field.name] == 'boolean') {
            value = this.settingData.query[field.name];
        }
        // Если пришло 1 / 0
        else if (
            this.settingData.query &&
            (this.settingData.query[field.name] == '0' || this.settingData.query[field.name] == '1')
        ) {
            value = this.settingData.query[field.name];
        }
        if (field.filter && field.filter.type == fieldsTypes.digit) {
            const formGroup = this.formBuilder.group({});
            this.formDynamic.addControl(field.name, formGroup);
            formGroup.addControl(
                'from',
                this.formBuilder.control(value && value.from ? value.from : null, [
                    (control: AbstractControl) =>
                        Validators.max(formGroup.get('to') ? formGroup.get('to').value : null)(control),
                ]),
            );
            formGroup.addControl(
                'to',
                this.formBuilder.control(value && value.to ? value.to : null, [
                    (control: AbstractControl) =>
                        Validators.min(formGroup.get('from') ? formGroup.get('from').value : null)(control),
                ]),
            );
        } else if (field.filter && field.filter.type == fieldsTypes.technicalProcess) {
            const formGroup = this.formBuilder.group({});
            this.formDynamic.addControl(field.name, formGroup);
            formGroup.addControl('tp', this.formBuilder.control(value && value.tp ? value.tp : null, []));
            formGroup.addControl(
                'regulation',
                this.formBuilder.control(value && value.regulation ? value.regulation : null, []),
            );
        } else if (field.filter && field.filter.type == fieldsTypes.access) {
            const formGroup = this.formBuilder.group({});
            this.formDynamic.addControl(field.name, formGroup);
            formGroup.addControl('user', this.formBuilder.control(value && value.user ? value.user : [], []));
            formGroup.addControl('subject', this.formBuilder.control(value && value.subject ? value.subject : [], []));
        } else if (field.filter && field.filter.type == fieldsTypes.address) {
            this.isFormAddress = true;
            const formGroup = this.formBuilder.group({
                region: [value && value.region ? value.region : [], []],
                district: [value && value.district ? value.district : [], []],
                city: [value && value.city ? value.city : [], []],
                region_name: [value && value.region_name ? value.region_name : null, []],
                district_name: [value && value.district_name ? value.district_name : null, []],
                city_name: [value && value.city_name ? value.city_name : null, []],
                street_1: [value && value.street_1 ? value.street_1 : null, []],
                street_2: [value && value.street_2 ? value.street_2 : null, []],
                street_3: [value && value.street_3 ? value.street_3 : null, []],
                including_additional_information: [
                    value && value.including_additional_information ? value.including_additional_information : false,
                    [],
                ],
                exact_match: [value && value.exact_match ? value.exact_match : false, []],
                building: [value && value.building ? value.building : null, []],
                apartment: [value && value.apartment ? value.apartment : null, []],
            });
            this.formDynamic.addControl(field.name, formGroup);
            if (value && value.region && value.region.length) {
                setTimeout(() => {
                    this.generateOptionsAddress(
                        field.name,
                        'region',
                        value && value.region ? value.region : null,
                        true,
                    );
                });
            }
            if (value && value.district && value.district.length) {
                setTimeout(() => {
                    this.generateOptionsAddress(
                        field.name,
                        'district',
                        value && value.district ? value.district : null,
                        true,
                    );
                });
            }
            formGroup
                .get('region')
                .valueChanges.pipe(takeUntil(this.#destroy$))
                .subscribe((value) => {
                    if (!this.isSetAddressChange) {
                        this.generateOptionsAddress(field.name, 'region', value);
                    }
                });
            formGroup
                .get('district')
                .valueChanges.pipe(takeUntil(this.#destroy$))
                .subscribe((value) => {
                    if (!this.isSetAddressChange) {
                        this.generateOptionsAddress(field.name, 'district', value);
                    }
                });
            formGroup
                .get('city')
                .valueChanges.pipe(takeUntil(this.#destroy$))
                .subscribe((value) => {
                    if (!this.isSetAddressChange) {
                        formGroup?.get('city_name')?.setValue(this.getAddressNamesByValue(value));
                    }
                });
        } else if (field.filter && field.filter.type == fieldsTypes.date) {
            const formGroup = this.formBuilder.group({});
            this.formDynamic.addControl(field.name, formGroup);
            formGroup.addControl('from', this.formBuilder.control(value && value.from ? value.from : null, []));
            formGroup.addControl('to', this.formBuilder.control(value && value.to ? value.to : null, []));
        } else {
            this.formDynamic.addControl(field.name, this.formBuilder.control(value, []));
        }
    }

    /**
     * Генерация опций селекта адреса
     * @param name имя поля
     * @param type тип поля
     * @param value значение селекта
     * @param isInit флаг инициализации
     * //TODO: сделать отдельный сервис для адреса
     */
    generateOptionsAddress(name: string, type: string, value: string[], isInit: boolean = false) {
        this.isSetAddressChange = true;
        let levels = null;
        if (type === 'region') {
            levels = '2,3,4,5';
        }
        if (type === 'district') {
            levels = '4,5,6';
        }
        if (value && value.length) {
            this.isLoadAddress.city[name] = true;
            if (type === 'region') {
                this.isLoadAddress.district[name] = true;
            }
            this.appService
                .getFiasAddressByApi({
                    type: '',
                    method: 'get_regions',
                    data: {
                        levels: levels,
                        parents: value,
                    },
                })
                .pipe(takeUntil(this.#destroy$))
                .subscribe((result) => {
                    const resultData = result.data ?? [];
                    if (type === 'region') {
                        this.addressData.district[name + '_district'] = [];
                        this.addressData.city[name + '_city'] = [];
                        this.addressData.city[name + '_city_region'] = [];
                        resultData.forEach((region) => {
                            if (region.level === 4 || region.level === 5) {
                                if (region.parents.length === 2) {
                                    this.addressData.city[name + '_city'].push({
                                        id: region.id,
                                        name: region.fullname,
                                    });
                                    this.addressData.city[name + '_city_region'].push({
                                        id: region.id,
                                        name: region.fullname,
                                    });
                                }
                            } else {
                                this.addressData.district[name + '_district'].push({
                                    id: region.id,
                                    name: region.fullname,
                                });
                            }
                            this.addressDataName[region.id] = region.fullname;
                        });
                        this.changeSelectsByTypes(name, type, isInit);
                        if (!isInit) {
                            this.formDynamic
                                ?.get(name)
                                ?.get('region_name')
                                ?.setValue(this.getAddressNamesByValue(value));
                        }
                    }
                    if (type === 'district') {
                        this.addressData.city[name + '_city'] = this.addressData?.city[name + '_city_region']
                            ? this.addressData?.city[name + '_city_region'].slice()
                            : [];
                        resultData.forEach((region) => {
                            this.addressData.city[name + '_city'].push({ id: region.id, name: region.fullname });
                            this.addressDataName[region.id] = region.fullname;
                        });
                        this.changeSelectsByTypes(name, type, isInit);
                        if (!isInit) {
                            this.formDynamic
                                ?.get(name)
                                ?.get('district_name')
                                ?.setValue(this.getAddressNamesByValue(value));
                        }
                    }
                    this.isSetAddressChange = false;
                    this.isLoadAddress.city[name] = false;
                    if (type === 'region') {
                        this.isLoadAddress.district[name] = false;
                    }
                });
        } else {
            if (type === 'region') {
                this.addressData.district[name + '_district'] = [];
                this.addressData.city[name + '_city_region'] = [];
            }
            this.addressData.city[name + '_city'] = [];
            if (type === 'district' && this.addressData?.city[name + '_city_region']?.length) {
                this.addressData.city[name + '_city'] = this.addressData?.city[name + '_city_region'].slice();
            }
            this.changeSelectsByTypes(name, type, isInit);
            if (!isInit) {
                this.formDynamic
                    ?.get(name)
                    ?.get(type + '_name')
                    ?.setValue(null);
            }
            this.isSetAddressChange = false;
        }
    }

    /**
     * Изменение селектов по типу
     * @param name имя поля
     * @param type тип
     * @param isInit флаг инициализации
     */
    changeSelectsByTypes(name: string, type: string, isInit: boolean = false) {
        this.changeOptionsAddress(name + '_city', this.addressData.city[name + '_city']);
        if (type == 'region') {
            this.changeOptionsAddress(name + '_district', this.addressData.district[name + '_district']);
            if (!isInit) {
                this.formDynamic?.get(name)?.get('district')?.setValue([]);
                this.formDynamic?.get(name)?.get('city')?.setValue([]);
                this.formDynamic?.get(name)?.get('district_name')?.setValue(null);
                this.formDynamic?.get(name)?.get('city_name')?.setValue(null);
            }
        }
        if (type == 'district') {
            if (!isInit) {
                this.formDynamic?.get(name)?.get('city')?.setValue([]);
                this.formDynamic?.get(name)?.get('city_name')?.setValue(null);
            }
        }
    }

    /**
     * Изменение опций селекта адреса
     * @param name имя поля
     * @param options опции
     */
    changeOptionsAddress(name: string, options: any[]) {
        if (this.dynamicFields) {
            this.dynamicFields.forEach((fieldItem: any) => {
                if (fieldItem.id && fieldItem.id == name) {
                    fieldItem.setOptions(options);
                }
            });
        }
    }

    /**
     * Получение имен адресов по набору UUID
     * @param value набор UUID
     */
    getAddressNamesByValue(value: string[]) {
        const address = [];
        if (value && value.length) {
            value.forEach((v) => {
                if (this.addressDataName[v]) {
                    address.push(this.addressDataName[v]);
                }
            });
        }
        return address.join(', ');
    }

    /**
     * Получение CSS для ширины для группы полей или поля
     */
    getWidthCss(item: any): string {
        const width = item.cols > 0 && item.cols <= 12 ? item.cols : 12;
        return `grid-column: span ${width} / span ${width}; overflow-x: hidden;`;
    }
}
